import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Search } from 'lucide-react';
import { ALL_NAV_ITEMS } from '@/layouts/nav';
import { cn } from '@/lib/cn';
import { Modal } from '@/components/ui';

/**
 * Keyboard jump to any screen. Replaces the previous header search box, which
 * looked functional but was wired to nothing.
 */
export function QuickNav({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  // Mounted only while open (see DashboardLayout), so state starts fresh.
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_NAV_ITEMS;
    return ALL_NAV_ITEMS.filter(
      (i) => i.label.toLowerCase().includes(q) || i.keywords?.includes(q) || i.href.includes(q)
    );
  }, [query]);

  const go = (href: string) => {
    navigate(href);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (results.length ? (c + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (results.length ? (c - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter' && results[cursor]) {
      e.preventDefault();
      go(results[cursor].href);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Go to" description="Jump to any screen" size="sm">
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-fg-faint"
            aria-hidden="true"
          />
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search screens…"
            aria-label="Search screens"
            aria-controls="quick-nav-results"
            className="h-10 w-full rounded-lg border border-line-strong bg-surface-2 pr-3 pl-9 text-sm text-fg outline-none placeholder:text-fg-faint focus:border-brand focus:ring-2 focus:ring-brand/35"
          />
        </div>

        <ul id="quick-nav-results" className="max-h-72 overflow-y-auto">
          {results.length === 0 && (
            <li className="px-2 py-6 text-center text-xs text-fg-subtle">No screens match “{query}”</li>
          )}
          {results.map((item, i) => (
            <li key={item.href}>
              <button
                type="button"
                onClick={() => go(item.href)}
                onMouseEnter={() => setCursor(i)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                  i === cursor ? 'bg-surface-2 text-fg' : 'text-fg-muted hover:bg-surface-2'
                )}
              >
                <item.icon className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />
                <span className="flex-1">{item.label}</span>
                <span className="font-tabular text-[11px] text-fg-faint">{item.href}</span>
                {i === cursor && <CornerDownLeft className="h-3 w-3 text-fg-faint" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
